import { Injectable } from '@angular/core';
import { CheckoutActions as CheckoutActionsV2 } from '@checkout-v2/data-access';
import { CheckoutActions } from '@checkout/data-access';
import { CityActions } from '@city/data-access';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AuthActions } from '@spartacus/core';
import { CustomUserActions, OccLoyaltyProgramService } from '@user/data-access';
import { Observable, of } from 'rxjs';
import { catchError, delay, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { BonusSystemFeatureAvailableService } from '../services';

const RETRY_DELAY_MS = 1500;

@Injectable()
export class LoyaltyProgramEffects {
  bonusBalance$: Observable<CustomUserActions.LoadBonusBalanceSuccess | CustomUserActions.LoadBonusBalanceFail> =
    createEffect(() => {
      return this.actions$.pipe(
        ofType(CustomUserActions.LOAD_BONUS_BALANCE),
        switchMap(() => this.bonusSystemFeatureAvailableService.isFeatureAvailable().pipe(take(1))),
        switchMap((isFeatureAvailable) => {
          if (isFeatureAvailable) {
            return this.occLoyaltyProgramService.loadBonusBalance().pipe(
              map((bonusBalance) => {
                if (!bonusBalance) {
                  return new CustomUserActions.LoadBonusBalanceFail();
                }

                return new CustomUserActions.LoadBonusBalanceSuccess({
                  segmentName: bonusBalance.segment?.name || '',
                  value: bonusBalance.bonusAmountInfo.value,
                  segmentCode: bonusBalance.segment?.code || '',
                });
              }),
              catchError(() => {
                return of(new CustomUserActions.LoadBonusBalanceFail());
              }),
            );
          } else {
            return of(new CustomUserActions.LoadBonusBalanceFail());
          }
        }),
      );
    });

  loadBonusBalanceOnLogin$: Observable<CustomUserActions.LoadBonusBalance> = createEffect(() =>
    this.actions$.pipe(
      ofType<AuthActions.Login>(AuthActions.LOGIN),
      switchMap(() => this.createRetryLoadBonusBalance()),
    ),
  );

  loadBonusBalanceOnCityChange$: Observable<CustomUserActions.LoadBonusBalance> = createEffect(() =>
    this.actions$.pipe(
      ofType(CityActions.SET_ACTIVE_BASE_STORE),
      map(() => new CustomUserActions.LoadBonusBalance()),
    ),
  );

  loadBonusBalanceOnOrder$: Observable<CustomUserActions.LoadBonusBalance> = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.PLACE_ORDER_SUCCESS, CheckoutActionsV2.PLACE_ORDER_SUCCESS),
      map(() => new CustomUserActions.LoadBonusBalance()),
    ),
  );

  resetBonusBalanceOnLogout$: Observable<CustomUserActions.LoadBonusBalanceReset> = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.LOGOUT),
      map(() => new CustomUserActions.LoadBonusBalanceReset()),
    ),
  );

  joinLoyaltyProgram$: Observable<
    CustomUserActions.JoinLoyaltyProgramSuccess | CustomUserActions.JoinLoyaltyProgramFail
  > = createEffect(() => {
    return this.actions$.pipe(
      ofType(CustomUserActions.JOIN_LOYALTY_PROGRAM),
      switchMap(() => {
        return this.occLoyaltyProgramService.joinLoyaltyProgram().pipe(
          map(() => new CustomUserActions.JoinLoyaltyProgramSuccess()),
          catchError(() => of(new CustomUserActions.JoinLoyaltyProgramFail())),
        );
      }),
    );
  });

  getLoyaltyQr$: Observable<CustomUserActions.LoadLoyaltyQrSuccess | CustomUserActions.LoadLoyaltyQrFail> =
    createEffect(() => {
      return this.actions$.pipe(
        ofType(CustomUserActions.LOAD_LOYALTY_QR),
        switchMap(() => {
          return this.occLoyaltyProgramService.getLoyaltyQr().pipe(
            map((res) => new CustomUserActions.LoadLoyaltyQrSuccess(res)),
            catchError(() => of(new CustomUserActions.LoadLoyaltyQrFail())),
          );
        }),
      );
    });

  checkSegmentAfterJoin$: Observable<CustomUserActions.LoadBonusBalance> = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomUserActions.JOIN_LOYALTY_PROGRAM_SUCCESS),
      switchMap(() => this.createRetryLoadBonusBalance()),
    ),
  );

  constructor(
    private actions$: Actions,
    private bonusSystemFeatureAvailableService: BonusSystemFeatureAvailableService,
    private occLoyaltyProgramService: OccLoyaltyProgramService,
  ) {}

  // В кейсе после СМС-логина, когда пользователь только вступил в ПЛ, нередко бэк
  // мог еще обрабатывать присвоение сегмента через ДХ и Промо и выдавал ошибку при первом запросе
  private createRetryLoadBonusBalance(): Observable<CustomUserActions.LoadBonusBalance> {
    const stopCondition$ = this.actions$.pipe(ofType(AuthActions.LOGOUT, CustomUserActions.LOAD_BONUS_BALANCE_SUCCESS));

    return of(new CustomUserActions.LoadBonusBalance()).pipe(delay(RETRY_DELAY_MS), takeUntil(stopCondition$));
  }
}
